import { Navigate, createBrowserRouter, type IndexRouteObject, type NonIndexRouteObject } from "react-router-dom";
import { importComponent } from "./helpers/importComponent";
import { ROUTES } from "./routes";
import Layout from "./layout";
import AuthGuard from "./components/AuthGuard";
import ErrorPage from "./pages/error";

type IndexAppRouteObject = IndexRouteObject & {
  permission?: string;
};

type NonIndexAppRouteObject = NonIndexRouteObject & {
  children?: AppRouteObject[];
  permission?: string;
};

export type AppRouteObject = IndexAppRouteObject | NonIndexAppRouteObject;

export const routerConfig: AppRouteObject[] = [
  {
    element: <Layout />,
    errorElement: <ErrorPage />,
    children: [
      // Public
      {
        element: <AuthGuard authLevel={"public"} />,
        children: [
          {
            path: ROUTES.HOME,
            lazy: importComponent(() => import("@/pages/App")),
          },
          {
            path: ROUTES.REGISTER,
            lazy: importComponent(() => import("@/pages/general/register")),
          },
          {
            path: ROUTES.LOGIN,
            lazy: importComponent(() => import("@/pages/general/login")),
          },
          {
            path: ROUTES.LOGOUT,
            lazy: importComponent(() => import("@/pages/general/logout")),
          },
          {
            path: ROUTES.ANONYMOUS_UPLOAD,
            lazy: importComponent(() => import("@/pages/general/anonymousUpload")),
          },
          {
            path: ROUTES.VERIFY_EMAIL,
            lazy: importComponent(() => import("@/pages/general/verifyEmail")),
          },
          {
            path: ROUTES.VERIFY_EMAIL_TOKEN,
            lazy: importComponent(() => import("@/pages/general/verifyEmailToken")),
          },
          {
            path: ROUTES.RESET_PASSWORD_EMAIL,
            lazy: importComponent(() => import("@/pages/reset-password/email")),
          },
          {
            path: ROUTES.RESET_PASSWORD_TOKEN,
            lazy: importComponent(() => import("@/pages/reset-password/token")),
          },
          {
            path: ROUTES.UNDER_REVIEW,
            lazy: importComponent(() => import("@/pages/general/underReview")),
          },
          {
            path: ROUTES.FAQ,
            lazy: importComponent(() => import("@/pages/general/frequently-asked-questions")),
          },
          {
            path: ROUTES.ABOUT_US,
            lazy: importComponent(() => import("@/pages/general/aboutUs")),
          },
          {
            path: ROUTES.ABOUT_PRODUCT,
            lazy: importComponent(() => import("@/pages/general/aboutProduct")),
          },
          {
            path: ROUTES.CONTACT_US,
            lazy: importComponent(() => import("@/pages/general/contactUs")),
          },
          {
            path: ROUTES.PRICING,
            lazy: importComponent(() => import("@/pages/general/priceList")),
          },
          {
            path: ROUTES.TERMS_AND_CONDITIONS,
            lazy: importComponent(() => import("@/pages/general/termsAndConditions")),
          },
          {
            path: ROUTES.PRIVACY_POLICY,
            lazy: importComponent(() => import("@/pages/general/privacyPolicy")),
          },
        ],
      },

      // Authentication and client type needed
      {
        element: <AuthGuard authLevel={"client"} />,
        children: [
          {
            path: ROUTES.ONBOARDING_BUILDING,
            lazy: importComponent(() => import("@/pages/onboarding/building")),
          },
          {
            path: ROUTES.ONBOARDING_COLLECTION_POINTS,
            lazy: importComponent(() => import("@/pages/onboarding/collectionPoints")),
          },
          {
            path: ROUTES.DASHBOARD,
            lazy: importComponent(() => import("@/pages/client/dashboard")),
          },
          {
            path: ROUTES.CLIENT_BUILDING_DETAILS,
            lazy: importComponent(() => import("@/pages/client/buildingDetail")),
          },
          {
            path: ROUTES.CLIENT_MEASUREMENT_HIERARCHY,
            lazy: importComponent(() => import("@/pages/client/measurementHierarchy")),
          },
          {
            path: ROUTES.CLIENT_COMPARISON,
            lazy: importComponent(() => import("@/pages/client/comparison")),
          },
          {
            path: ROUTES.CLIENT_BUILDING_CARD,
            lazy: importComponent(() => import("@/pages/client/buildingCard.tsx")),
          },
          {
            path: ROUTES.CLIENT_UPLOAD_INVOICE,
            lazy: importComponent(() => import("@/pages/client/uploadInvoice")),
          },
          {
            path: ROUTES.CLIENT_UPLOAD_INVOICE_COL_POINT,
            lazy: importComponent(() => import("@/pages/client/uploadInvoice")),
          },
          {
            path: ROUTES.CLIENT_EDIT_INVOICE,
            lazy: importComponent(() => import("@/pages/client/uploadInvoice")),
          },
          {
            path: ROUTES.CLIENT_EDIT_INVOICE_COL_POINT,
            lazy: importComponent(() => import("@/pages/client/uploadInvoice")),
          },
          {
            path: ROUTES.CLIENT_PRODUCTION_INVOICE,
            lazy: importComponent(() => import("@/pages/client/productionInvoice")),
          },
          {
            path: ROUTES.CLIENT_UPLOAD_CONSUMPTION,
            lazy: importComponent(() => import("@/pages/client/uploadConsumption")),
          },
          {
            path: ROUTES.CLIENT_UPLOAD_CONSUMPTION_COL_POINT,
            lazy: importComponent(() => import("@/pages/client/uploadConsumption")),
          },
          {
            path: ROUTES.CLIENT_EDIT_CONSUMPTION,
            lazy: importComponent(() => import("@/pages/client/uploadConsumption")),
          },
          {
            path: ROUTES.CLIENT_EDIT_CONSUMPTION_COL_POINT,
            lazy: importComponent(() => import("@/pages/client/uploadConsumption")),
          },
          {
            path: ROUTES.CLIENT_DATA_QUEUE,
            lazy: importComponent(() => import("@/pages/client/dataQueue")),
          },
          {
            path: ROUTES.CLIENT_ADD_BUILDING,
            lazy: importComponent(() => import("@/pages/client/buildingForm")),
          },
          {
            path: ROUTES.CLIENT_EDIT_BUILDING,
            lazy: importComponent(() => import("@/pages/client/buildingForm")),
          },
          {
            path: ROUTES.CLIENT_ADD_COLLECTION_POINT,
            lazy: importComponent(() => import("@/pages/client/collectionPointForm")),
          },
          {
            path: ROUTES.CLIENT_EDIT_COLLECTION_POINT,
            lazy: importComponent(() => import("@/pages/client/collectionPointForm")),
          },
          {
            path: ROUTES.CLIENT_BUILDING_GROUPS,
            lazy: importComponent(() => import("@/pages/client/buildingGroups")),
          },
          {
            path: ROUTES.CLIENT_SUBSCRIPTION,
            lazy: importComponent(() => import("@/pages/client/subscription")),
          },
          {
            path: ROUTES.CLIENT_PRICELIST,
            lazy: importComponent(() => import("@/pages/general/priceList")),
          },
          {
            path: ROUTES.CLIENT_CHECKOUT_PAID,
            lazy: importComponent(() => import("@/pages/client/checkout/paid")),
          },
          {
            path: ROUTES.CLIENT_CHECKOUT_CANCELLED,
            lazy: importComponent(() => import("@/pages/client/checkout/cancelled")),
          },
          {
            path: "/client/users",
            element: <Navigate to={ROUTES.CLIENT_SETTINGS} replace />,
          },
          {
            path: ROUTES.CLIENT_SETTINGS,
            lazy: importComponent(() => import("@/pages/client/settings")),
          }
        ],
      },

      // Authorization and admin type needed
      {
        element: <AuthGuard authLevel={"admin"} />,
        children: [
          {
            path: ROUTES.ADMIN_DASHBOARD,
            lazy: importComponent(() => import("@/pages/admin/dashboard")),
          },
          {
            path: ROUTES.ADMIN_CLIENTS,
            lazy: importComponent(() => import("@/pages/admin/clients")),
            permission: "clients",
          },
          {
            path: ROUTES.ADMIN_EXTERNAL_INTEGRATIONS,
            lazy: importComponent(() => import("@/pages/admin/externalIntegrations")),
            permission: "clients",
          },
          {
            path: ROUTES.ADMIN_CLIENT_DETAIL,
            lazy: importComponent(() => import("@/pages/admin/clientDetail")),
            permission: "clients",

          },
          {
            path: ROUTES.ADMIN_BUILDING_DETAIL,
            lazy: importComponent(() => import("@/pages/admin/buildingDetail")),
            permission: "clients",
          },
          {
            path: ROUTES.ADMIN_BUILDING_CARD,
            lazy: importComponent(() => import("@/pages/admin/buildingCard.tsx")),
            permission: "clients",
          },
          {
            path: ROUTES.ADMIN_ADMINS,
            lazy: importComponent(() => import("@/pages/admin/admins")),
            permission: "admins",
          },
          {
            path: ROUTES.ADMIN_ADMIN_DETAIL,
            lazy: importComponent(() => import("@/pages/admin/adminDetail")),
            permission: "admins",
          },
          {
            path: ROUTES.ADMIN_FREQUENTLY_ASKED_QUESTIONS,
            lazy: importComponent(() => import("@/pages/admin/faq")),
            permission: "faqs",
          },
          {
            path: ROUTES.ADMIN_FREQUENTLY_ASKED_QUESTIONS_FORM,
            lazy: importComponent(() => import("@/pages/admin/faqDetail")),
            permission: "faqs",
          },
          {
            path: ROUTES.ADMIN_DELETED_ITEMS,
            lazy: importComponent(() => import("@/pages/admin/deletedItems")),
            permission: "deleted-items",
          }
        ],
      },
    ],
  },
];

const router = createBrowserRouter(routerConfig);

export default router;
